import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Boxes, AlertTriangle, AlertOctagon, Clock4, Store, PackagePlus, Package,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Zone manager Inventory section: summary + low-stock alerts + per-branch table + pending requests + restock dialog.
 * Extracted from ZoneManagerDashboard.jsx for maintainability.
 * Consumes the payload from GET /api/zones/dashboard/inventory.
 */
export default function InventorySection({ inventory, onChanged, getAuthHeaders }) {
  const branches = inventory?.branches || [];
  const summary = inventory?.summary || {};
  const lowAlerts = inventory?.low_stock_alerts || [];
  const pending = inventory?.pending_requests || [];

  const [selectedBranchId, setSelectedBranchId] = useState(branches[0]?.branch_id || '');
  const [reqDialog, setReqDialog] = useState({ open: false, item: null, branch: null });
  const [reqQty, setReqQty] = useState(0);
  const [reqNotes, setReqNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!selectedBranchId && branches.length > 0) {
      setSelectedBranchId(branches[0].branch_id);
    }
  }, [branches, selectedBranchId]);

  const currentBranch = branches.find(b => b.branch_id === selectedBranchId) || branches[0];

  const openRequest = (item, branch) => {
    const suggested = Math.max((item.min_stock || 5) * 2 - (item.stock || 0), 1);
    setReqDialog({ open: true, item, branch });
    setReqQty(suggested);
    setReqNotes('');
  };

  const submitRequest = async () => {
    if (!reqDialog.item || !reqDialog.branch) return;
    if (!reqQty || reqQty <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/zones/restock-requests`, {
        product_id: reqDialog.item.product_id,
        branch_id: reqDialog.branch.branch_id,
        requested_quantity: Number(reqQty),
        notes: reqNotes,
      }, getAuthHeaders());
      toast.success('Solicitud de reposición creada');
      setReqDialog({ open: false, item: null, branch: null });
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (branches.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-12 text-center" data-testid="inventory-empty">
        <Boxes className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-400">No hay sucursales asignadas a tu zona</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="inv-total">
          <div className="flex items-center gap-2 mb-2">
            <Boxes className="w-4 h-4 text-gold" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Productos</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_items || 0}</p>
          <p className="text-xs text-zinc-500">en zona</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="inv-low">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Bajo Stock</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{summary.low_stock_items || 0}</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="inv-out">
          <div className="flex items-center gap-2 mb-2">
            <AlertOctagon className="w-4 h-4 text-red-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Agotados</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{summary.out_of_stock || 0}</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="inv-pending">
          <div className="flex items-center gap-2 mb-2">
            <Clock4 className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Solicitudes</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">{summary.pending_requests || 0}</p>
          <p className="text-xs text-zinc-500">pendientes</p>
        </Card>
      </div>

      {/* Low Stock Alerts */}
      {lowAlerts.length > 0 && (
        <Card className="bg-yellow-950/20 border-yellow-900/50 p-5" data-testid="low-stock-alerts">
          <h3 className="text-sm text-yellow-400 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alertas de Bajo Inventario ({lowAlerts.length})
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {lowAlerts.map((a) => (
              <div key={a.inventory_id} className="flex items-center justify-between bg-zinc-900/50 rounded-sm p-3 border border-yellow-900/30">
                <div className="flex-1">
                  <p className="text-sm text-white">{a.product_name}</p>
                  <p className="text-xs text-zinc-500">{a.branch_name}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-zinc-500">Stock / Mín</p>
                    <p className={`text-sm font-semibold ${a.stock === 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {a.stock} / {a.min_stock}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openRequest(
                      { product_id: a.product_id, product_name: a.product_name, stock: a.stock, min_stock: a.min_stock },
                      { branch_id: a.branch_id, branch_name: a.branch_name }
                    )}
                    className="border-yellow-700 text-yellow-400 hover:bg-yellow-900/30 text-xs"
                    data-testid={`alert-request-${a.inventory_id}`}
                  >
                    <PackagePlus className="w-3 h-3 mr-1" />
                    Solicitar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Branch Selector + Inventory Table */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="branch-inventory-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm text-zinc-400 flex items-center gap-2">
            <Store className="w-4 h-4" />
            Inventario por Sucursal
          </h3>
          <div className="flex gap-1 bg-zinc-800/50 rounded-sm p-1" data-testid="branch-selector">
            {branches.map(b => (
              <button
                key={b.branch_id}
                onClick={() => setSelectedBranchId(b.branch_id)}
                className={`px-3 py-1.5 rounded-sm text-xs transition-colors ${
                  selectedBranchId === b.branch_id ? 'bg-gold/20 text-gold' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`branch-tab-${b.branch_id}`}
              >
                {b.branch_name}
                {b.low_stock_items > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 rounded-full">
                    {b.low_stock_items}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {currentBranch && currentBranch.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Producto</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Stock</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Mín</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Precio</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Estado</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Acción</th>
                </tr>
              </thead>
              <tbody>
                {currentBranch.items.map((item) => (
                  <tr key={item.inventory_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-3 px-3 text-zinc-200">{item.product_name}</td>
                    <td className={`text-right py-3 px-3 font-semibold ${item.is_out ? 'text-red-400' : item.is_low ? 'text-yellow-400' : 'text-white'}`}>
                      {item.stock}
                    </td>
                    <td className="text-right py-3 px-3 text-zinc-500">{item.min_stock}</td>
                    <td className="text-right py-3 px-3 text-zinc-400">${item.price}</td>
                    <td className="text-center py-3 px-3">
                      {item.is_out ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400">Agotado</span>
                      ) : item.is_low ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">Bajo</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-400">OK</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openRequest(item, currentBranch)}
                        className="text-zinc-400 hover:text-gold text-xs h-7"
                        data-testid={`request-restock-${item.inventory_id}`}
                      >
                        <PackagePlus className="w-3 h-3 mr-1" />
                        Solicitar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No hay inventario registrado en esta sucursal</p>
          </div>
        )}
      </Card>

      {/* Pending Restock Requests */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="pending-requests-card">
        <h3 className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
          <Clock4 className="w-4 h-4" />
          Solicitudes Pendientes ({pending.length})
        </h3>
        {pending.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">No tienes solicitudes pendientes</p>
        ) : (
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-zinc-800/30 rounded-sm p-3" data-testid={`pending-request-${r.id}`}>
                <div className="flex-1">
                  <p className="text-sm text-white">{r.product_name} <span className="text-zinc-500">× {r.requested_quantity}</span></p>
                  <p className="text-xs text-zinc-500">{r.branch_name} · {new Date(r.created_at).toLocaleDateString('es-MX')}</p>
                  {r.notes && <p className="text-xs text-zinc-600 mt-1 italic">&quot;{r.notes}&quot;</p>}
                </div>
                <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400">Pendiente</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Request Dialog */}
      <Dialog open={reqDialog.open} onOpenChange={(o) => !o && setReqDialog({ open: false, item: null, branch: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="restock-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Solicitar Reposición</DialogTitle>
          </DialogHeader>
          {reqDialog.item && reqDialog.branch && (
            <div className="space-y-4 py-2">
              <div className="bg-zinc-800/50 rounded-sm p-3">
                <p className="text-sm text-white font-medium">{reqDialog.item.product_name}</p>
                <p className="text-xs text-zinc-500">{reqDialog.branch.branch_name}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Stock actual: <span className="text-yellow-400">{reqDialog.item.stock}</span> · Mín: {reqDialog.item.min_stock}
                </p>
              </div>
              <div>
                <Label htmlFor="req-qty" className="text-zinc-300">Cantidad solicitada</Label>
                <Input
                  id="req-qty"
                  type="number"
                  min={1}
                  value={reqQty}
                  onChange={(e) => setReqQty(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="req-qty-input"
                />
              </div>
              <div>
                <Label htmlFor="req-notes" className="text-zinc-300">Notas (opcional)</Label>
                <Textarea
                  id="req-notes"
                  value={reqNotes}
                  onChange={(e) => setReqNotes(e.target.value)}
                  placeholder="Ej: necesario antes del fin de semana"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="req-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReqDialog({ open: false, item: null, branch: null })} data-testid="req-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={submitRequest} disabled={submitting} className="bg-gold hover:bg-gold/90 text-black" data-testid="req-submit-btn">
              {submitting ? 'Enviando...' : 'Enviar Solicitud'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
